'use client';

import { useState } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import type { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';

import statue from '@/public/fabio-affiliate/statue-card.png';
import dotCards from '@/public/fabio-affiliate/dots-card.png';
import logoAlt from '@/public/fabio-affiliate/logo-alt.svg';

export default function ComparisonSection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  const [selectedTab, setSelectedTab] = useState<'iq' | 'other'>('iq');

  if (
    !data?.comparisonChip &&
    !data?.comparisonHeading &&
    !data?.comparisonColumns?.length &&
    !data?.comparisonRows?.length
  ) {
    return null;
  }

  const columns = data?.comparisonColumns ?? [];
  const rows = data?.comparisonRows ?? [];
  const cards = [
    {
      type: 'features' as const,
      column: columns[0],
      bgClass: ' bg-white',
      titleClass: 'text-[#0E2E36]',
      textClass: 'text-black/50 uppercase font-[500]  text-[2.25rem]',
      dividerClass: 'border-[#E5E5E5]',
    },
    {
      type: 'iq' as const,
      column: columns[1],
      bgClass: 'bg-[#112E27]',
      titleClass: 'text-white ',
      textClass: 'text-white !text-[2.25rem] font-[400]',
      dividerClass: 'border-white/10',
    },
    {
      type: 'other' as const,
      column: columns[2],
      bgClass: 'bg-[#112E27]/[0.08]',
      titleClass: 'text-[#0E2E36]',
      textClass: 'text-black !text-[2.25rem] font-[400]',
      dividerClass: 'border-[#112E27]/10',
    },
  ];

  return (
    <section className="lg:px-0 lg:py-[15rem] py-[10rem] px-[2rem]">
      <div className="mx-auto w-[min(140rem,100%)]">
        <div className="flex flex-col items-center text-center mb-[5rem] gap-[1rem]">
          {data?.comparisonChip && (
            <span className="font-medium tracking-wider uppercase text-[1.4rem] text-[#0E2E36]">
              {data.comparisonChip}
            </span>
          )}
          {data?.comparisonHeading && (
            <div className="richtext richtext-center prose-h2:text-black prose-h2:font-geist prose-h2:font-[500] prose-h2:leading-[115%] prose-h2:tracking-[-0.25rem] lg:prose-h2:tracking-[-0.4rem]">
              <PortableText
                value={data.comparisonHeading}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {(columns.length > 0 || rows.length > 0) && (
          <>
            <div className="hidden grid-cols-1 md:grid-cols-3 lg:grid gap-[2rem] h-[66.875rem]">
              {cards.map((card) => (
                <div
                  key={card.type}
                  className={`rounded-[1.5rem] px-[3rem] pt-[2rem] pb-[2.75rem] relative justify-between overflow-clip flex flex-col ${card.bgClass}`}
                >
                  {card.type === 'iq' && (
                    <>
                      <Image
                        src={statue}
                        alt=""
                        quality={100}
                        className="absolute top-0 right-0 pointer-events-none z-[5] w-[24.25rem]"
                      />
                      <Image
                        src={dotCards}
                        alt=""
                        quality={100}
                        className="object-cover absolute inset-0 w-full h-full pointer-events-none z-[1]"
                      />
                    </>
                  )}
                  <div
                    className={` relative z-[8]  flex items-center gap-[0.75rem] min-h-[6rem] ${card.titleClass}`}
                  >
                    {card.column?.logo ? (
                      <GroqImage
                        src={card.column.logo as any}
                        alt={card.column.label ?? ''}
                        className=""
                      />
                    ) : (
                      card.column?.label && (
                        <span className="text-[3rem] font-[600] leading-[112%]">
                          {card.column.label}
                        </span>
                      )
                    )}
                  </div>
                  <div className="flex relative flex-col z-[8]">
                    {rows.map((row, i) => {
                      const content =
                        card.type === 'features'
                          ? row?.feature
                          : card.type === 'iq'
                            ? row?.values?.[0]?.text
                            : row?.values?.[1]?.text;
                      return (
                        <div
                          key={`${card.type}-row-${i}`}
                          className={` py-[1.5rem] font-geist  min-h-[5.5rem] flex items-center border-b last:border-b-0 ${card.textClass} ${card.dividerClass}
                                  `}
                        >
                          {content ?? ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:hidden">
              <div className="flex gap-[1rem] mb-[1.5rem] bg-black/[3%] p-[.5rem] border border-black/5 rounded-[0.75rem]">
                {cards.slice(1).map((card) => {
                  const isActive = card.type === selectedTab;
                  return (
                    <button
                      key={card.type}
                      type="button"
                      onClick={() =>
                        setSelectedTab(card.type as 'iq' | 'other')
                      }
                      className={`flex flex-1 items-center justify-center gap-[0.75rem] py-[2rem] rounded-[.5rem] transition-colors ${
                        isActive
                          ? 'bg-[#112E27] text-white '
                          : ' text-[#0E2E36] '
                      }`}
                    >
                      {card.column?.logo ? (
                        isActive ? (
                          <GroqImage
                            src={card.column.logo as any}
                            alt={card.column.label ?? ''}
                            className="w-[14.25rem]"
                          />
                        ) : (
                          <Image
                            src={logoAlt}
                            alt=""
                            className="pointer-events-none"
                          />
                        )
                      ) : (
                        card.column?.label && (
                          <span className="text-[2rem] font-[600] leading-[112%]">
                            {card.column.label}
                          </span>
                        )
                      )}
                    </button>
                  );
                })}
              </div>
              {cards.slice(0, 1).map((card) => {
                const valueIndex = selectedTab === 'iq' ? 0 : 1;
                return (
                  <div
                    key={card.type}
                    className="flex relative flex-col justify-between border rounded-[.5rem] px-[2rem] py-[1.75rem] overflow-clip bg-[#228E71]/5 border-black/5"
                  >
                    <div className="flex relative items-center z-[8] gap-[0.75rem] min-h-[6rem] text-[#0E2E36]">
                      {card.column?.logo ? (
                        <GroqImage
                          src={card.column.logo as any}
                          alt={card.column.label ?? ''}
                          className="w-[14.25rem]"
                        />
                      ) : (
                        card.column?.label && (
                          <span className="text-[3rem] font-[600] leading-[112%]">
                            {card.column.label}
                          </span>
                        )
                      )}
                    </div>
                    <div className="flex relative flex-col z-[8]">
                      {rows.map((row, i) => (
                        <div
                          key={`${card.type}-row-${i}`}
                          className="flex justify-between items-center uppercase border-b last:border-b-0 gap-[1rem] text-[#103037] text-[2rem] font-[500] py-[1.5rem] font-geist min-h-[5.5rem] border-[#E5E5E5]"
                        >
                          <span>{row?.feature ?? ''}</span>
                          <span className="text-right normal-case text-black/50 text-[2rem] max-w-[50%]">
                            {row?.values?.[valueIndex]?.text ?? ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
